import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Ban, SquareArrowOutUpRight, MessageSquareReply, Plus, Tags } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { usePermissions } from '@/hooks/use-permissions';
import { ConfirmToggleDialog } from '@/components/shared/confirm-toggle-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAiSettings, useTicketPreprocessingConfig, useUpdateTicketPreprocessingConfig } from '@/hooks';
import { useBrandSettings, useUpdateBrandSettings } from '@/hooks/use-brand-settings';
import { hasBrandBusinessTime } from '@/lib/ai-settings-defaults';
import { getApiErrorMessage } from '@/lib/utils';
import { MAX_PATTERN_ENTRIES, MAX_PATTERN_LENGTH, MessageStripPatternsEditor } from '@/modules/brand';
import {
	ChipInput,
	Field,
	SaveBar,
	SETTINGS_INPUT_CLASS,
	SETTINGS_PILL_BUTTON_CLASS,
	SETTINGS_TOOLTIP_CLASS,
	SettingsBody,
	SettingsCard,
	SettingsCardSkeleton,
	SettingsDivider,
	SettingsError,
	SettingsSection,
	ToggleRow
} from '@/modules/settings';
import type { TicketPreprocessingConfig } from '@/types';
import { FormatToolbar } from '@/components/message-editor/format-toolbar';
import { EmojiButton, LinkButton } from '@/components/message-editor/insert-actions';
import { useMessageEditor } from '@/components/message-editor/use-message-editor';
import '@/components/message-editor/editor.css';

const DEFAULT_CONFIG: TicketPreprocessingConfig = {
	enabled: false,
	addTags: [],
	removeTags: [],
	autoAssign: false,
	assigneeEmail: null,
	autoReplyInHoursEnabled: false,
	autoReplyInHoursMessage: '',
	autoReplyOutOfHoursEnabled: false,
	autoReplyOutOfHoursMessage: ''
};

/** Mirrors MAX_AUTO_REPLY_MESSAGE_LENGTH on the backend. */
const MAX_AUTO_REPLY_LENGTH = 5000;
const MAX_TAGS = 50;
const MAX_TAG_LENGTH = 50;

const AUTO_REPLY_WINDOWS = [
	{
		enabledKey: 'autoReplyInHoursEnabled',
		messageKey: 'autoReplyInHoursMessage',
		label: 'Within business hours',
		description: 'Sent when the ticket arrives during your business hours.'
	},
	{
		enabledKey: 'autoReplyOutOfHoursEnabled',
		messageKey: 'autoReplyOutOfHoursMessage',
		label: 'Outside business hours',
		description: 'Sent when the ticket arrives outside your business hours.'
	}
] as const;

const TAG_LISTS = [
	{ key: 'addTags', other: 'removeTags', title: 'Add tag on processing', description: 'These tags are added when Kim pre-processes a ticket.' },
	{ key: 'removeTags', other: 'addTags', title: 'Remove tag on processing', description: 'These tags are removed when Kim pre-processes a ticket.' }
] as const;

/** The Sentinel workspace composer's editor, without the send controls. */
function AutoReplyEditor({
	editorKey,
	initialMarkdown,
	label,
	onChange
}: {
	editorKey: string;
	initialMarkdown: string;
	label: string;
	onChange: (markdown: string) => void;
}) {
	const editor = useMessageEditor({ initialMarkdown, editorKey, placeholder: 'Type the auto-reply', onChange });
	return (
		<div className='flex flex-col gap-1 rounded-2xl border border-gray-500 bg-white p-2'>
			<div ref={editor.hostRef} aria-label={label} className='message-editor w-full p-1.5' />
			<div className='flex items-center gap-1'>
				<FormatToolbar editor={editor} />
				<EmojiButton editor={editor} />
				<LinkButton editor={editor} />
			</div>
		</div>
	);
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Handling › Pre-Processing: what Kim does to a ticket before answering it, plus message cleanup. */
export default function TicketPreprocessingPage() {
	const { data: settings, isLoading, isError } = useTicketPreprocessingConfig();
	const updateMutation = useUpdateTicketPreprocessingConfig();
	const { data: brandSettings, isLoading: brandLoading, isError: brandError } = useBrandSettings();
	const updateBrand = useUpdateBrandSettings();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');
	// Auto-replies are chosen by business hours, so they stay locked until the brand has a timezone and
	// business hours. Locked only once the lookup has CONFIRMED they are missing, so a slow or failed
	// lookup never blocks an unrelated save (the backend still refuses to enable one without them).
	const { data: aiSettings, isLoading: isAiSettingsLoading, isError: isAiSettingsError } = useAiSettings();
	const autoReplyLocked = !isAiSettingsLoading && !isAiSettingsError && !hasBrandBusinessTime(aiSettings);

	const [config, setConfig] = useState<TicketPreprocessingConfig>(DEFAULT_CONFIG);
	const [savedConfig, setSavedConfig] = useState<TicketPreprocessingConfig>(DEFAULT_CONFIG);
	const [patterns, setPatterns] = useState<string[]>([]);
	const [savedPatterns, setSavedPatterns] = useState<string[]>([]);
	// The editors load their Markdown once per key: bump it whenever the config is replaced wholesale.
	const [editorVersion, setEditorVersion] = useState(0);
	// Switches that open a confirmation first (null = no dialog open).
	const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
	const [pendingAutoReply, setPendingAutoReply] = useState<{
		window: (typeof AUTO_REPLY_WINDOWS)[number];
		value: boolean;
	} | null>(null);

	useEffect(() => {
		const initial = { ...DEFAULT_CONFIG, ...settings };
		setConfig(initial);
		setSavedConfig(initial);
		setEditorVersion((v) => v + 1);
	}, [settings]);

	useEffect(() => {
		const loaded = brandSettings?.messageStripPatterns ?? [];
		setPatterns(loaded);
		setSavedPatterns(loaded);
	}, [brandSettings]);

	const configDirty = !sameJson(config, savedConfig);
	const patternsDirty = !sameJson(patterns, savedPatterns);
	const isSaving = updateMutation.isPending || updateBrand.isPending;
	const hasOverLimitPattern = patterns.some((p) => p.length > MAX_PATTERN_LENGTH);

	/** Mirrors the backend's tag rules: 1–50 chars, at most 50, none shared with the other list. */
	const commitTags = (next: string[], otherList: string[], key: 'addTags' | 'removeTags'): boolean => {
		const trimmed = next.map((t) => t.trim()).filter(Boolean);
		if (trimmed.some((t) => t.length > MAX_TAG_LENGTH)) {
			toast.error(`Tags must be 1–${MAX_TAG_LENGTH} characters.`);
			return false;
		}
		if (trimmed.length > MAX_TAGS) {
			toast.error(`You can add at most ${MAX_TAGS} tags per list.`);
			return false;
		}
		const conflict = trimmed.find((t) => otherList.some((o) => o.toLowerCase() === t.toLowerCase()));
		if (conflict) {
			toast.error(`"${conflict}" is already used in the other list.`);
			return false;
		}
		const seen = new Set<string>();
		const deduped = trimmed.filter((t) => {
			const k = t.toLowerCase();
			if (seen.has(k)) return false;
			seen.add(k);
			return true;
		});
		setConfig((prev) => ({ ...prev, [key]: deduped }));
		return true;
	};

	const handleCancel = () => {
		setConfig(savedConfig);
		setPatterns(savedPatterns);
		setEditorVersion((v) => v + 1);
	};

	/** The master switch saves on confirm: only `enabled` changes, so other unsaved edits stay unsaved. */
	const confirmEnabled = async () => {
		if (pendingEnabled === null) return;
		try {
			const saved = await updateMutation.mutateAsync({ ...savedConfig, enabled: pendingEnabled });
			setSavedConfig(saved);
			setConfig((prev) => ({ ...prev, enabled: saved.enabled }));
			toast.success(saved.enabled ? 'Ticket preprocessing enabled' : 'Ticket preprocessing disabled');
		} catch {
			toast.error('Failed to update ticket preprocessing. Please try again.');
		} finally {
			setPendingEnabled(null);
		}
	};

	const handleSave = async () => {
		const assigneeEmail = config.assigneeEmail?.trim() || null;
		// Only a config change is checked, so a pattern-only edit is never blocked by an unrelated setting.
		if (configDirty) {
			if (assigneeEmail && !EMAIL_PATTERN.test(assigneeEmail)) {
				toast.error('Enter a valid agent email, or leave it empty to use the Kim Gorgias user.');
				return;
			}
			if (autoReplyLocked && (config.autoReplyInHoursEnabled || config.autoReplyOutOfHoursEnabled)) {
				toast.error('Set your timezone and business hours in My Brand before enabling auto-replies.');
				return;
			}
			for (const { enabledKey, messageKey, label } of AUTO_REPLY_WINDOWS) {
				const message = config[messageKey].trim();
				if (config[enabledKey] && !message) {
					toast.error(`Write the "${label.toLowerCase()}" auto-reply, or turn it off.`);
					return;
				}
				if (message.length > MAX_AUTO_REPLY_LENGTH) {
					toast.error(`Auto-replies must be at most ${MAX_AUTO_REPLY_LENGTH} characters.`);
					return;
				}
			}
		}
		const cleanedPatterns = patterns.filter((p) => p.trim().length > 0);
		if (cleanedPatterns.length > MAX_PATTERN_ENTRIES) {
			toast.error(`At most ${MAX_PATTERN_ENTRIES} patterns are allowed.`);
			return;
		}
		try {
			if (configDirty) {
				const saved = await updateMutation.mutateAsync({ ...config, assigneeEmail });
				setSavedConfig(saved);
				setConfig(saved);
				setEditorVersion((v) => v + 1);
			}
			if (patternsDirty) {
				await updateBrand.mutateAsync({ messageStripPatterns: cleanedPatterns });
				setPatterns(cleanedPatterns);
				setSavedPatterns(cleanedPatterns);
			}
			toast.success('Pre-processing saved');
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to save pre-processing. Please try again.'));
		}
	};

	return (
		<>
			<SettingsBody>
				{isError ? (
					<SettingsError />
				) : isLoading ? (
					<SettingsSection>
						<SettingsCardSkeleton rows={3} />
					</SettingsSection>
				) : (
					<SettingsSection>
						<SettingsCard>
							<div className='flex flex-col gap-6'>
								<ToggleRow
									title='Ticket pre-processing'
									description='When on, Kim runs these steps on every ticket it processes.'
									action={
										<Switch
											checked={config.enabled}
											disabled={!canEdit || updateMutation.isPending}
											onCheckedChange={setPendingEnabled}
											aria-label='Ticket Preprocessing'
										/>
									}
								/>
								<div className='flex items-center gap-4 rounded-2xl border border-gray-300 bg-gray-50 p-4'>
									<ToggleRow
										title='Bypass for certain intents?'
										description='Skip pre-processing for tickets classified with these intents. No tags are changed and the ticket is not assigned to Kim.'
									/>
									<Link
										to='/settings/intents?tab=active'
										aria-label='Choose the intents that skip pre-processing'
										className='flex size-7 shrink-0 items-center justify-center rounded-full text-indigo-500 hover:bg-gray-100'
									>
										<SquareArrowOutUpRight className='size-3.5' />
									</Link>
								</div>
							</div>

							{config.enabled && (
								<>
									<SettingsDivider />
									<ToggleRow
										title='Assign on processing'
										description='Assign tickets Kim processes to the Kim user while they are still unassigned. A ticket an agent holds is left alone.'
										checked={config.autoAssign}
										disabled={!canEdit || isSaving}
										onCheckedChange={(next) => setConfig((prev) => ({ ...prev, autoAssign: next }))}
									/>
									{config.autoAssign && (
										<Field
											label='Gorgias agent email override (optional)'
											htmlFor='sentinel-assignee-email'
											hint='By default tickets go to the Gorgias user created when the integration was connected. Enter an agent email to assign to that agent instead.'
										>
											<input
												id='sentinel-assignee-email'
												type='email'
												value={config.assigneeEmail ?? ''}
												placeholder='Leave empty to use the Kim Gorgias user'
												disabled={!canEdit || isSaving}
												onChange={(e) => setConfig((prev) => ({ ...prev, assigneeEmail: e.target.value || null }))}
												className={SETTINGS_INPUT_CLASS}
											/>
										</Field>
									)}

									<SettingsDivider />
									<ToggleRow
										title='First Acknowledgement Response'
										description="Send the customer a fixed reply once their ticket passes every check, based on your business hours. It is not sent when Kim will answer the ticket itself, when an agent has already replied, or more than once per ticket."
									/>
									{autoReplyLocked && (
										<p className='rounded-[12px] bg-amber-100 px-3 py-2 text-[12px] leading-[18px] text-ink'>
											Set your timezone and your business hours in{' '}
											<Link to='/settings/brand' className='font-medium text-indigo-500 hover:underline'>
												My Brand
											</Link>{' '}
											to use auto-replies.
										</p>
									)}
									{AUTO_REPLY_WINDOWS.map((window) => {
										const { enabledKey, messageKey, label, description } = window;
										return (
											<div key={enabledKey} className='flex flex-col gap-2 rounded-2xl border border-gray-300 bg-gray-50 p-4'>
												<div className='flex items-center justify-between gap-4'>
													<div className='flex flex-col gap-0.5'>
														<p className='text-[13px] font-medium leading-[18px] text-ink'>{label}</p>
														<p className='text-[12px] leading-4 text-gray-900'>{description}</p>
													</div>
													<Tooltip>
														<TooltipTrigger asChild>
															{/* A disabled switch swallows pointer events, so the tooltip hangs off this wrapper. */}
															<span tabIndex={autoReplyLocked ? 0 : -1}>
																<Switch
																	checked={config[enabledKey]}
																	// Locked only for switching ON — one already on can always be turned off.
																	disabled={!canEdit || isSaving || (autoReplyLocked && !config[enabledKey])}
																	onCheckedChange={(next) => setPendingAutoReply({ window, value: next })}
																	aria-label={`Auto-reply ${label.toLowerCase()}`}
																/>
															</span>
														</TooltipTrigger>
														{autoReplyLocked && (
															<TooltipContent variant='dark' className={SETTINGS_TOOLTIP_CLASS}>
																Set your timezone and business hours in My Brand.
															</TooltipContent>
														)}
													</Tooltip>
												</div>
												{config[enabledKey] && (
													<AutoReplyEditor
														editorKey={`${messageKey}:${editorVersion}`}
														initialMarkdown={config[messageKey]}
														label={`Auto-reply ${label.toLowerCase()}`}
														onChange={(markdown) => setConfig((prev) => ({ ...prev, [messageKey]: markdown }))}
													/>
												)}
											</div>
										);
									})}
								</>
							)}
						</SettingsCard>
					</SettingsSection>
				)}

				<SettingsSection label='Message cleanup'>
					{brandError ? (
						<SettingsError />
					) : brandLoading ? (
						<SettingsCardSkeleton rows={1} />
					) : (
						<SettingsCard>
							<ToggleRow
								title='Ticket strip pattern'
								description='Remove boilerplate (mailing-list footers, signatures) from customer messages before they reach the AI. Each entry is matched as plain text.'
							/>
							<MessageStripPatternsEditor patterns={patterns} onChange={setPatterns} disabled={!canEdit || isSaving} hideAddButton />
							<button
								type='button'
								onClick={() => setPatterns((prev) => [...prev, ''])}
								disabled={!canEdit || isSaving || patterns.length >= MAX_PATTERN_ENTRIES}
								className={`${SETTINGS_PILL_BUTTON_CLASS} w-fit`}
							>
								<Plus className='size-3.5' />
								Pattern
							</button>
						</SettingsCard>
					)}
				</SettingsSection>

				{!isLoading && !isError && config.enabled && (
					<SettingsSection label='Tags'>
						<SettingsCard>
							{TAG_LISTS.map(({ key, other, title, description }, idx) => (
								<div key={key} className='flex flex-col gap-4'>
									{idx > 0 && <SettingsDivider />}
									<ToggleRow title={title} description={description} />
									<ChipInput
										values={config[key]}
										onAdd={(value) => commitTags([...config[key], value], config[other], key)}
										onRemove={(value) => commitTags(config[key].filter((t) => t !== value), config[other], key)}
										placeholder='Type a tag and press Enter'
										aria-label={title}
										disabled={!canEdit}
									/>
								</div>
							))}
						</SettingsCard>
					</SettingsSection>
				)}
			</SettingsBody>

			<ConfirmToggleDialog
				pendingValue={pendingEnabled}
				icon={pendingEnabled === false ? Ban : Tags}
				title={pendingEnabled === false ? 'Disable Ticket Preprocessing?' : 'Enable Ticket Preprocessing?'}
				description={
					pendingEnabled === false ? (
						<p>
							Kim will stop sending auto-replies, assigning tickets to the Kim agent, and adding or removing tags for this brand.
							Kim&apos;s AI responses are not affected.
						</p>
					) : (
						<p>
							The auto-replies, Kim agent assignment and tag changes configured on this page will start applying to new tickets
							for this brand.
						</p>
					)
				}
				confirmLabel={pendingEnabled === false ? 'Disable' : 'Enable'}
				isPending={updateMutation.isPending}
				onConfirm={confirmEnabled}
				onCancel={() => setPendingEnabled(null)}
			/>

			<ConfirmToggleDialog
				pendingValue={pendingAutoReply?.value ?? null}
				icon={pendingAutoReply?.value === false ? Ban : MessageSquareReply}
				title={`${pendingAutoReply?.value === false ? 'Disable' : 'Enable'} auto-reply ${pendingAutoReply?.window.label.toLowerCase() ?? ''}?`}
				description={
					pendingAutoReply?.value === false ? (
						<p>
							Tickets that arrive {pendingAutoReply.window.label.toLowerCase()} will no longer get this auto-reply. Kim&apos;s AI
							responses and the other preprocessing steps are not affected.
						</p>
					) : (
						<>
							<p>
								When a ticket arrives {pendingAutoReply?.window.label.toLowerCase()} and passes every check (blocked accounts,
								inbound filters, AI operating hours, channel and intent filters, and tickets assigned to a human agent), Kim sends
								this message to the customer.
							</p>
							<p>It is sent once per ticket, only on the customer&apos;s first public message (private notes don&apos;t count).</p>
							<div>
								<p className='font-medium'>If the ticket&apos;s intent is enabled for AI automation:</p>
								<ul className='list-disc pl-4'>
									<li>The auto-reply is delayed until Kim&apos;s automated response is ready.</li>
									<li>If Kim sends the automated response, the auto-reply is not sent.</li>
									<li>If the automated response fails a quality gate and is not sent, the auto-reply is sent instead.</li>
								</ul>
							</div>
							<p>The timezone and business hours come from My Brand. Save the page to apply.</p>
						</>
					)
				}
				confirmLabel={pendingAutoReply?.value === false ? 'Disable auto-reply' : 'Enable auto-reply'}
				onConfirm={() => {
					if (pendingAutoReply) {
						const { window, value } = pendingAutoReply;
						setConfig((prev) => ({ ...prev, [window.enabledKey]: value }));
					}
					setPendingAutoReply(null);
				}}
				onCancel={() => setPendingAutoReply(null)}
			/>

			<SaveBar
				visible={configDirty || patternsDirty}
				saving={isSaving}
				canSave={canEdit && !hasOverLimitPattern}
				onCancel={handleCancel}
				onSave={() => void handleSave()}
			/>
		</>
	);
}
